import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
    Network,
    LineChart,
    ShieldAlert,
    Waves,
    BrainCircuit,
    Compass,
    User,
    Sparkles,
} from "lucide-react";

/* ---------- Layer data ---------- */
const LAYERS = [
    {
        id: "structure",
        title: "Market Structure",
        tag: "Topology",
        desc: "Liquidity, venue, and flow topology — the shape of every market AIMA reads, mapped continuously.",
        icon: Network,
    },
    {
        id: "forecast",
        title: "Forecasting",
        tag: "Predictive",
        desc: "Probabilistic price and volatility paths, expressed as distributions across multiple horizons.",
        icon: LineChart,
    },
    {
        id: "risk",
        title: "Risk",
        tag: "Regime",
        desc: "Regime detection, tail exposure, and drawdown sensitivity with quantified confidence.",
        icon: ShieldAlert,
    },
    {
        id: "sentiment",
        title: "Sentiment",
        tag: "Signal",
        desc: "Signal extraction from social, on-chain, news, and disclosure streams — denoised continuously.",
        icon: Waves,
    },
    {
        id: "explain",
        title: "Explainability",
        tag: "Provenance",
        desc: "Reasoning chains, provenance, and counter-evidence travel with every output as first-class data.",
        icon: BrainCircuit,
    },
    {
        id: "strategy",
        title: "Strategy",
        tag: "Decision",
        desc: "Scenario construction and decision support that reasons across horizons, not just headlines.",
        icon: Compass,
    },
    {
        id: "personalize",
        title: "Personalization",
        tag: "Context",
        desc: "Intelligence tuned to mandate, horizon, and risk tolerance — the same engine, your shape.",
        icon: User,
    },
];

const AUTO_INTERVAL = 5500;

/* ---------- Large per-layer visualizations ---------- */
const StructureViz = () => (
    <svg viewBox="0 0 480 320" className="w-full h-full">
        <defs>
            <linearGradient id="sl" x1="0" x2="1">
                <stop offset="0%" stopColor="#6E3CFF" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.85" />
            </linearGradient>
        </defs>
        {(() => {
            const nodes = [
                [60, 80, "BTC"], [140, 50, "ETH"], [220, 100, "SOL"],
                [300, 60, "NDX"], [380, 110, "SPX"], [120, 200, "EUR"],
                [220, 240, "JPY"], [340, 220, "XAU"], [420, 180, "OIL"],
                [240, 160, ""],
            ];
            const edges = [
                [0,1],[1,2],[2,3],[3,4],[2,9],[5,9],[6,9],[1,9],[7,9],[8,9],[9,3],[9,4],[5,6],[6,7],[7,8]
            ];
            return (
                <g>
                    {edges.map(([a, b], i) => (
                        <motion.line
                            key={i}
                            x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]}
                            stroke="url(#sl)" strokeWidth="0.8" opacity="0.45"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1, opacity: [0.2, 0.55, 0.2] }}
                            transition={{
                                pathLength: { duration: 1.4, delay: i * 0.05 },
                                opacity: { duration: 5 + (i % 4), repeat: Infinity, ease: "easeInOut", delay: i * 0.2 },
                            }}
                        />
                    ))}
                    {nodes.map(([x, y, label], i) => (
                        <g key={i}>
                            <motion.circle
                                cx={x} cy={y} r={i === 9 ? 7 : 4.5}
                                fill={i === 9 ? "#00E5FF" : "#0a0a0c"}
                                stroke={i === 9 ? "#00E5FF" : "rgba(0,229,255,0.7)"} strokeWidth="1.2"
                                animate={{ opacity: [0.7, 1, 0.7], scale: [0.95, 1.1, 0.95] }}
                                transition={{ duration: 2.5 + (i % 3), repeat: Infinity, delay: i * 0.15 }}
                                style={{ originX: `${x}px`, originY: `${y}px` }}
                            />
                            {label && (
                                <text x={x + 9} y={y + 3} fill="rgba(255,255,255,0.55)" fontSize="9" fontFamily="JetBrains Mono">
                                    {label}
                                </text>
                            )}
                        </g>
                    ))}
                </g>
            );
        })()}
    </svg>
);

const ForecastViz = () => (
    <svg viewBox="0 0 480 320" className="w-full h-full">
        <defs>
            <linearGradient id="ff" x1="0" x2="1">
                <stop offset="0%" stopColor="#6E3CFF" />
                <stop offset="100%" stopColor="#00E5FF" />
            </linearGradient>
            <linearGradient id="ffan" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
            </linearGradient>
        </defs>
        {/* axes */}
        <line x1="40" y1="280" x2="460" y2="280" stroke="rgba(255,255,255,0.1)" strokeWidth="0.6" />
        <line x1="240" y1="40" x2="240" y2="280" stroke="rgba(255,255,255,0.08)" strokeWidth="0.6" strokeDasharray="3 4" />
        <text x="246" y="50" fill="rgba(255,255,255,0.4)" fontSize="9" fontFamily="JetBrains Mono">NOW</text>
        {/* historical actual */}
        <motion.path
            d="M40 220 L80 210 L120 200 L160 180 L200 165 L240 150"
            stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.6 }}
        />
        {/* upper fan */}
        <motion.path
            d="M240 150 L280 130 L320 100 L360 80 L400 60 L440 50 L440 110 L400 110 L360 130 L320 145 L280 150 L240 150 Z"
            fill="url(#ffan)"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 1 }}
        />
        {/* lower fan */}
        <motion.path
            d="M240 150 L280 165 L320 175 L360 200 L400 220 L440 240 L440 195 L400 175 L360 160 L320 155 L280 152 L240 150 Z"
            fill="url(#ffan)" opacity="0.7"
            initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ duration: 1.2, delay: 1.1 }}
        />
        {/* main forecast */}
        <motion.path
            d="M240 150 L280 142 L320 130 L360 110 L400 95 L440 80"
            stroke="url(#ff)" strokeWidth="2" fill="none" strokeLinecap="round"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
        />
        <motion.circle cx="240" cy="150" r="4" fill="#fff" animate={{ scale: [0.9, 1.3, 0.9] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: "240px", originY: "150px" }} />
        <motion.circle cx="440" cy="80" r="5" fill="#00E5FF" animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: "440px", originY: "80px" }} />
        <text x="446" y="76" fill="#00E5FF" fontSize="9" fontFamily="JetBrains Mono" textAnchor="end">P50 +2.4%</text>
    </svg>
);

const RiskViz = () => {
    // 8 cols x 5 rows heatmap
    const data = Array.from({ length: 40 }, (_, i) => {
        const r = ((i * 9301 + 49297) % 233280) / 233280;
        return r;
    });
    return (
        <svg viewBox="0 0 480 320" className="w-full h-full">
            {data.map((v, i) => {
                const col = i % 8;
                const row = Math.floor(i / 8);
                const x = 40 + col * 50;
                const y = 60 + row * 42;
                const opacity = 0.15 + v * 0.7;
                const isHot = v > 0.7;
                return (
                    <motion.rect
                        key={i}
                        x={x} y={y} width="44" height="36"
                        fill={isHot ? "#6E3CFF" : "#00E5FF"}
                        opacity={opacity}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity }}
                        transition={{ duration: 0.6, delay: i * 0.015 }}
                        style={{ originX: `${x + 22}px`, originY: `${y + 18}px` }}
                    />
                );
            })}
            <text x="40" y="50" fill="rgba(255,255,255,0.5)" fontSize="9" fontFamily="JetBrains Mono" letterSpacing="2">REGIME · 5D × 8 ASSETS</text>
            {/* moving regime indicator */}
            <motion.rect
                x="36" y="56" width="404" height="214"
                fill="none" stroke="rgba(0,229,255,0.5)" strokeWidth="1"
                animate={{ y: [56, 92, 134, 176, 56], opacity: [0, 1, 1, 1, 0] }}
                transition={{ duration: 8, repeat: Infinity, times: [0, 0.25, 0.5, 0.75, 1] }}
            />
        </svg>
    );
};

const SentimentViz = () => (
    <svg viewBox="0 0 480 320" className="w-full h-full">
        <defs>
            <linearGradient id="sw" x1="0" x2="1">
                <stop offset="0%" stopColor="#6E3CFF" stopOpacity="0.4" />
                <stop offset="50%" stopColor="#00E5FF" />
                <stop offset="100%" stopColor="#6E3CFF" stopOpacity="0.4" />
            </linearGradient>
        </defs>
        {/* labels */}
        {["SOCIAL", "ON-CHAIN", "NEWS", "FILINGS"].map((l, i) => (
            <text key={l} x="40" y={70 + i * 60} fill="rgba(255,255,255,0.4)" fontSize="9" fontFamily="JetBrains Mono">
                {l}
            </text>
        ))}
        {[60, 120, 180, 240].map((y, i) => (
            <motion.path
                key={y}
                d={`M120 ${y} Q 160 ${y - 18} 200 ${y} T 280 ${y} T 360 ${y} T 440 ${y}`}
                stroke="url(#sw)"
                strokeWidth={1 + (i === 1 ? 1 : 0)}
                fill="none"
                strokeLinecap="round"
                opacity={0.6 + (i === 1 ? 0.4 : 0)}
                animate={{ x: [-40, 0, -40] }}
                transition={{ duration: 6 + i, repeat: Infinity, ease: "easeInOut" }}
            />
        ))}
        {/* merged signal */}
        <text x="240" y="290" textAnchor="middle" fill="rgba(0,229,255,0.7)" fontSize="9" fontFamily="JetBrains Mono" letterSpacing="2">
            ⌁ MERGED SIGNAL +0.32
        </text>
        <motion.path
            d="M120 280 Q 200 268 280 274 T 440 280"
            stroke="#00E5FF" strokeWidth="1.6" fill="none"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 0.4 }}
        />
    </svg>
);

const ExplainViz = () => (
    <svg viewBox="0 0 480 320" className="w-full h-full">
        {(() => {
            const cols = [
                { x: 60, ys: [80, 160, 240], label: "INPUTS" },
                { x: 200, ys: [60, 120, 180, 240], label: "AGENTS" },
                { x: 340, ys: [120, 200], label: "CONSENSUS" },
                { x: 440, ys: [160], label: "OUTPUT" },
            ];
            const links = [];
            cols[0].ys.forEach((y1) =>
                cols[1].ys.forEach((y2) => links.push([cols[0].x, y1, cols[1].x, y2, "01"]))
            );
            cols[1].ys.forEach((y1) =>
                cols[2].ys.forEach((y2) => links.push([cols[1].x, y1, cols[2].x, y2, "02"]))
            );
            cols[2].ys.forEach((y1) =>
                cols[3].ys.forEach((y2) => links.push([cols[2].x, y1, cols[3].x, y2, "03"]))
            );
            return (
                <>
                    {links.map((l, i) => (
                        <motion.line
                            key={i}
                            x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]}
                            stroke="rgba(0,229,255,0.55)" strokeWidth="0.6"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1, opacity: [0.2, 0.6, 0.2] }}
                            transition={{
                                pathLength: { duration: 1.2, delay: i * 0.04 },
                                opacity: { duration: 4, repeat: Infinity, delay: i * 0.08 },
                            }}
                        />
                    ))}
                    {cols.map((c) =>
                        c.ys.map((y, j) => (
                            <circle key={`${c.x}-${j}`} cx={c.x} cy={y} r="3.5" fill="#fff" opacity="0.85" />
                        ))
                    )}
                    {cols.map((c) => (
                        <text key={c.label} x={c.x} y="40" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="9" fontFamily="JetBrains Mono" letterSpacing="2">
                            {c.label}
                        </text>
                    ))}
                </>
            );
        })()}
    </svg>
);

const StrategyViz = () => {
    const candles = Array.from({ length: 14 }, (_, i) => {
        const r = ((i * 9301 + 49297 + 50) % 233280) / 233280;
        const o = 200 - r * 80;
        const c = 200 - ((r * 1.4) % 1) * 100;
        const h = Math.min(o, c) - 20 - r * 10;
        const l = Math.max(o, c) + 12 + r * 10;
        return { x: 60 + i * 26, o, c, h, l };
    });
    return (
        <svg viewBox="0 0 480 320" className="w-full h-full">
            <line x1="40" y1="280" x2="460" y2="280" stroke="rgba(255,255,255,0.1)" />
            {candles.map((c, i) => {
                const up = c.c < c.o;
                const color = up ? "#00E5FF" : "#A78BFA";
                return (
                    <motion.g key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.06 }}>
                        <line x1={c.x} y1={c.h} x2={c.x} y2={c.l} stroke={color} strokeWidth="1" />
                        <rect x={c.x - 5} y={Math.min(c.o, c.c)} width="10" height={Math.abs(c.o - c.c) || 2} fill={color} opacity="0.85" />
                    </motion.g>
                );
            })}
            {/* scenario branches projecting forward */}
            {[
                { y2: 100, label: "P75 +3.2%", color: "#00E5FF" },
                { y2: 140, label: "P50 +1.4%", color: "#fff" },
                { y2: 200, label: "P25 −0.6%", color: "#A78BFA" },
            ].map((s, i) => (
                <g key={i}>
                    <motion.line
                        x1={candles[candles.length - 1].x} y1="160" x2="450" y2={s.y2}
                        stroke={s.color} strokeWidth="0.9" strokeDasharray="2 4"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.8 }}
                        transition={{ duration: 1.4, delay: 1 + i * 0.2 }}
                    />
                    <text x="455" y={s.y2 + 3} fill={s.color} fontSize="9" fontFamily="JetBrains Mono" textAnchor="end">
                        {s.label}
                    </text>
                </g>
            ))}
        </svg>
    );
};

const PersonalizeViz = () => {
    // Radar/spider chart
    const axes = ["HORIZON", "RISK TOL.", "MANDATE", "VENUE", "SECTOR", "STYLE"];
    const pts = [0.65, 0.4, 0.85, 0.55, 0.7, 0.5];
    const cx = 240, cy = 160, r = 110;
    const pointFor = (i, val) => {
        const angle = (i * 360) / axes.length - 90;
        const a = (angle * Math.PI) / 180;
        return [cx + Math.cos(a) * r * val, cy + Math.sin(a) * r * val];
    };
    const polyPts = pts.map((v, i) => pointFor(i, v).join(",")).join(" ");
    return (
        <svg viewBox="0 0 480 320" className="w-full h-full">
            {[0.25, 0.5, 0.75, 1].map((s, i) => (
                <polygon
                    key={i}
                    points={axes.map((_, j) => pointFor(j, s).join(",")).join(" ")}
                    fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.6"
                />
            ))}
            {axes.map((a, i) => {
                const [x, y] = pointFor(i, 1.05);
                return (
                    <g key={a}>
                        <line x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="0.6" />
                        <text x={x} y={y} fill="rgba(255,255,255,0.45)" fontSize="9" fontFamily="JetBrains Mono" textAnchor="middle" dy={y > cy ? 12 : -4}>
                            {a}
                        </text>
                    </g>
                );
            })}
            <motion.polygon
                points={polyPts}
                fill="rgba(0,229,255,0.18)" stroke="#00E5FF" strokeWidth="1.4"
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                style={{ originX: `${cx}px`, originY: `${cy}px` }}
            />
            {pts.map((v, i) => {
                const [x, y] = pointFor(i, v);
                return <motion.circle key={i} cx={x} cy={y} r="3.5" fill="#fff" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4 + i * 0.08 }} />;
            })}
            <motion.circle cx={cx} cy={cy} r="3" fill="#00E5FF" animate={{ scale: [0.9, 1.4, 0.9] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: `${cx}px`, originY: `${cy}px` }} />
        </svg>
    );
};

const VIZ_MAP = {
    structure: StructureViz,
    forecast: ForecastViz,
    risk: RiskViz,
    sentiment: SentimentViz,
    explain: ExplainViz,
    strategy: StrategyViz,
    personalize: PersonalizeViz,
};

/* ---------- Main showcase component ---------- */
export const LayerShowcase = () => {
    const { t } = useTranslation();
    const texts = t("landing.layersData", { returnObjects: true });
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const containerRef = useRef(null);
    const [minListH, setMinListH] = useState(0);

    useEffect(() => {
        if (paused) return;
        const id = setTimeout(() => {
            setActive((a) => (a + 1) % LAYERS.length);
        }, AUTO_INTERVAL);
        return () => clearTimeout(id);
    }, [active, paused]);

    /*
     * Layout-stability fix: the auto-advancing list expands one description per
     * layer, and descriptions differ in length — so the list (and the whole
     * page below it) used to grow/shrink every cycle. We measure the worst case
     * (longest description + active title) via hidden ghost markup and pin the
     * list to that height, so the expand animation plays inside reserved space
     * and the page never moves.
     */
    useLayoutEffect(() => {
        const measure = () => {
            const ghost = document.getElementById("layers-ghost-list");
            const descGhost = document.getElementById("layers-ghost-descs");
            if (!ghost || !descGhost) return;
            const items = ghost.querySelectorAll("[data-ghost-item]");
            const inactive = ghost.querySelector("[data-ghost-inactive]");
            const activeItem = ghost.querySelector("[data-ghost-active]");
            if (!items.length || !inactive || !activeItem) return;
            let base = 0;
            items.forEach((it) => { base += it.offsetHeight; });
            const activeDelta = activeItem.offsetHeight - inactive.offsetHeight;
            const colWidth = ghost.querySelector("[data-ghost-flexcol]")?.offsetWidth || 0;
            if (colWidth) descGhost.style.width = `${colWidth}px`;
            const maxDesc = Math.max(
                ...Array.from(descGhost.children).map((c) => c.offsetHeight),
                0
            );
            setMinListH(base + Math.max(activeDelta, 0) + maxDesc + 12);
        };
        measure();
        const t1 = setTimeout(measure, 600); // settle after webfonts
        let fontReady = false;
        if (document.fonts && document.fonts.ready) {
            fontReady = true;
            document.fonts.ready.then(measure).catch(() => {});
        }
        window.addEventListener("resize", measure);
        return () => {
            clearTimeout(t1);
            window.removeEventListener("resize", measure);
        };
    }, [texts]);

    const ActiveViz = VIZ_MAP[LAYERS[active].id];
    const ActiveLayer = LAYERS[active];
    const ActiveIcon = ActiveLayer.icon;
    const activeText = texts[active];

    return (
        <div
            ref={containerRef}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
            data-testid="layer-showcase"
        >
            {/* Left: layer list */}
            <div className="lg:col-span-5 flex flex-col relative">
                {/* Hidden ghost markup: measures worst-case list height (longest
                    description + active title size) so the real list can be pinned
                    and the page never shifts while layers auto-advance. */}
                <div aria-hidden="true" className="absolute inset-x-0 top-0 invisible pointer-events-none select-none">
                    <ul id="layers-ghost-list" className="border-t border-white/[0.06]">
                        {LAYERS.map((l, i) => (
                            <li key={l.id} data-ghost-item {...(i === 0 ? { "data-ghost-inactive": true } : {})} className="border-b border-white/[0.06]">
                                <div className="w-full text-left py-5 md:py-6 px-2 md:px-3">
                                    <div className="flex items-start gap-5 pl-4">
                                        <span className="font-mono text-[11px] uppercase tracking-[0.22em] mt-1">0{i + 1}</span>
                                        <div className="flex-1" {...(i === 0 ? { "data-ghost-flexcol": true } : {})}>
                                            <div className="flex items-center justify-between gap-3">
                                                <h3 className="font-cabinet tracking-tight text-white/55 text-xl md:text-2xl">{texts[i].title}</h3>
                                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] shrink-0">{texts[i].tag}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                        {/* active-state probe (same content as item 1, active title size) */}
                        <li data-ghost-active className="border-b border-white/[0.06]">
                            <div className="w-full text-left py-5 md:py-6 px-2 md:px-3">
                                <div className="flex items-start gap-5 pl-4">
                                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] mt-1">01</span>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between gap-3">
                                            <h3 className="font-cabinet tracking-tight text-white text-2xl md:text-[28px]">{texts[0].title}</h3>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] shrink-0">{texts[0].tag}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <div id="layers-ghost-descs">
                        {LAYERS.map((l, i) => (
                            <p key={l.id} className="font-plex text-[14.5px] leading-relaxed max-w-md">{texts[i].desc}</p>
                        ))}
                    </div>
                </div>

                <ul className="border-t border-white/[0.06]" style={{ minHeight: minListH || undefined }}>
                    {LAYERS.map((l, i) => {
                        const isActive = active === i;
                        return (
                            <li key={l.id} className="border-b border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setActive(i)}
                                    data-testid={`layer-step-${l.id}`}
                                    aria-pressed={isActive}
                                    className={`relative w-full text-left py-5 md:py-6 px-2 md:px-3 transition-colors group ${
                                        isActive ? "" : "hover:bg-white/[0.015]"
                                    }`}
                                >
                                    {/* active accent bar */}
                                    <motion.span
                                        initial={false}
                                        animate={{ scaleY: isActive ? 1 : 0, opacity: isActive ? 1 : 0 }}
                                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                        className="absolute left-0 top-3 bottom-3 w-px bg-gradient-to-b from-cyan via-violet to-cyan/0 origin-top"
                                    />
                                    <div className="flex items-start gap-5 pl-4">
                                        <span
                                            className={`font-mono text-[11px] uppercase tracking-[0.22em] mt-1 transition-colors ${
                                                isActive ? "text-cyan" : "text-white/35 group-hover:text-white/55"
                                            }`}
                                        >
                                            0{i + 1}
                                        </span>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between gap-3">
                                                <h3
                                                    className={`font-cabinet tracking-tight transition-all ${
                                                        isActive
                                                            ? "text-white text-2xl md:text-[28px]"
                                                            : "text-white/55 group-hover:text-white/85 text-xl md:text-2xl"
                                                    }`}
                                                >
                                                    {texts[i].title}
                                                </h3>
                                                <span
                                                    className={`font-mono text-[10px] uppercase tracking-[0.22em] shrink-0 transition-colors ${
                                                        isActive ? "text-white/60" : "text-white/25 group-hover:text-white/45"
                                                    }`}
                                                >
                                                    {texts[i].tag}
                                                </span>
                                            </div>
                                            <AnimatePresence initial={false}>
                                                {isActive && (
                                                    <motion.p
                                                        key="desc"
                                                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                                        animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                                                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                                        className="font-plex text-[14.5px] text-white/60 leading-relaxed max-w-md overflow-hidden"
                                                    >
                                                        {texts[i].desc}
                                                    </motion.p>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {/* Auto-advance progress */}
                <div className="mt-8 h-px bg-white/[0.06] overflow-hidden">
                    <motion.div
                        key={`${active}-${paused}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: paused ? 0 : 1 }}
                        transition={{ duration: paused ? 0.2 : AUTO_INTERVAL / 1000, ease: "linear" }}
                        className="h-full origin-left bg-gradient-to-r from-cyan via-violet to-transparent"
                    />
                </div>
            </div>

            {/* Right: large visualization */}
            <div className="lg:col-span-7">
                <div className="relative aspect-[480/360] w-full border border-white/[0.06] bg-graphite overflow-hidden">
                    {/* ambient inside panel */}
                    <motion.div
                        key={`bg-${active}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse 70% 70% at 50% 50%, rgba(110,60,255,0.18), transparent 75%)",
                        }}
                    />
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 grid-lines-bg opacity-30 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_50%,#000,transparent_85%)]"
                    />

                    {/* header */}
                    <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 h-10 border-b border-white/[0.06] bg-black/30 backdrop-blur-sm">
                        <div className="flex items-center gap-2">
                            <ActiveIcon className="w-4 h-4 text-cyan" strokeWidth={1.5} />
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/70">
                                {t("landing.layersViz.layer")} 0{active + 1} · {activeText.title}
                            </span>
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan/80 flex items-center gap-1.5">
                            <motion.span
                                animate={{ opacity: [0.4, 1, 0.4] }}
                                transition={{ duration: 1.6, repeat: Infinity }}
                                className="w-1.5 h-1.5 rounded-full bg-cyan"
                            />
                            {activeText.tag}
                        </span>
                    </div>

                    {/* viz */}
                    <div className="absolute inset-0 pt-10 pb-10 px-4">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={ActiveLayer.id}
                                initial={{ opacity: 0, scale: 0.97, filter: "blur(8px)" }}
                                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                                exit={{ opacity: 0, scale: 1.02, filter: "blur(8px)" }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                className="w-full h-full"
                            >
                                <ActiveViz />
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* footer with consensus chip */}
                    <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-5 h-10 border-t border-white/[0.06] bg-black/30 backdrop-blur-sm">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                            {t("landing.layersViz.reconcile")}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 inline-flex items-center gap-2">
                            <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                            {t("landing.layersViz.core")}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LayerShowcase;
